import type { UnitGroup, UnitRoster } from './units';

export type BattleRound = {
    round: number;
    attackerPower: number;
    defenderPower: number;
    attackerRoll: number;
    defenderRoll: number;
    attackerKills: number;
    defenderKills: number;
    attackerRemaining: number;
    defenderRemaining: number;
};

export type BattleOutcome = {
    winner: 'attacker' | 'defender' | 'draw';
    attackerRemaining: number;
    defenderRemaining: number;
    attackerLosses: number;
    defenderLosses: number;
};

export type BattleState = {
    id: string;
    tileId: string;
    attacker: UnitGroup[];
    defender: UnitGroup[];
    attackerStart: UnitGroup[];
    defenderStart: UnitGroup[];
    rounds: BattleRound[];
    totalRounds: number;
    roundIndex: number;
    roundTick: number;
    ticksPerRound: number;
    rngState: number;
    status: 'in_progress' | 'complete';
    outcome?: BattleOutcome;
};

export type BattleConfig = {
    roundTicks: number;
    unitsPerRound: number;
    minRounds: number;
    maxRounds: number;
    rollMin: number;
    rollMax: number;
};

export type BattleReport = {
    id: string;
    tileId: string;
    outcome: BattleOutcome;
    rounds: BattleRound[];
    attackerStart: UnitGroup[];
    defenderStart: UnitGroup[];
    attackerFinal: UnitGroup[];
    defenderFinal: UnitGroup[];
    startedAt: number;
    endedAt: number;
};

export function createBattleState(args: {
    id: string;
    tileId: string;
    attacker: UnitGroup[];
    defender: UnitGroup[];
    config: BattleConfig;
    roster: UnitRoster;
    rngSeed: number;
}): BattleState {
    const totalUnits =
        args.roster.totalUnits(args.attacker) + args.roster.totalUnits(args.defender);
    const totalRounds = clamp(
        Math.ceil(totalUnits / Math.max(1, args.config.unitsPerRound)),
        args.config.minRounds,
        args.config.maxRounds,
    );
    return {
        id: args.id,
        tileId: args.tileId,
        attacker: cloneGroups(args.attacker),
        defender: cloneGroups(args.defender),
        attackerStart: cloneGroups(args.attacker),
        defenderStart: cloneGroups(args.defender),
        rounds: [],
        totalRounds,
        roundIndex: 0,
        roundTick: 0,
        ticksPerRound: Math.max(1, Math.round(args.config.roundTicks)),
        rngState: args.rngSeed >>> 0,
        status: 'in_progress',
    };
}

export function advanceBattle(
    battle: BattleState,
    roster: UnitRoster,
    config: BattleConfig,
): boolean {
    if (battle.status !== 'in_progress') return false;
    battle.roundTick += 1;
    if (battle.roundTick < battle.ticksPerRound) return false;

    battle.roundTick = 0;
    const round = resolveRound(battle, roster, config, () => nextRand(battle));
    battle.rounds.push(round);
    battle.roundIndex += 1;

    if (round.attackerRemaining <= 0 || round.defenderRemaining <= 0) {
        completeBattle(battle, roster);
        return true;
    }
    if (battle.roundIndex >= battle.totalRounds) {
        completeBattle(battle, roster);
        return true;
    }
    return true;
}

export function resolveRound(
    battle: { attacker: UnitGroup[]; defender: UnitGroup[]; roundIndex: number },
    roster: UnitRoster,
    config: BattleConfig,
    rng: () => number,
): BattleRound {
    const attackerPower = roster.totalPower(battle.attacker);
    const defenderPower = roster.totalPower(battle.defender);
    const attackerRoll = lerp(config.rollMin, config.rollMax, clamp01(rng()));
    const defenderRoll = lerp(config.rollMin, config.rollMax, clamp01(rng()));
    const attackerUnits = roster.totalUnits(battle.attacker);
    const defenderUnits = roster.totalUnits(battle.defender);
    const attackerKills = Math.min(defenderUnits, Math.floor(attackerPower * attackerRoll));
    const defenderKills = Math.min(attackerUnits, Math.floor(defenderPower * defenderRoll));
    applyCasualties(battle.defender, attackerKills);
    applyCasualties(battle.attacker, defenderKills);
    const attackerRemaining = roster.totalUnits(battle.attacker);
    const defenderRemaining = roster.totalUnits(battle.defender);

    return {
        round: battle.roundIndex + 1,
        attackerPower,
        defenderPower,
        attackerRoll,
        defenderRoll,
        attackerKills,
        defenderKills,
        attackerRemaining,
        defenderRemaining,
    };
}

export function buildBattleReport(
    battle: BattleState,
    roster: UnitRoster,
    id: string,
    startedAt: number,
    endedAt: number,
): BattleReport {
    if (!battle.outcome) {
        completeBattle(battle, roster);
    }
    return {
        id,
        tileId: battle.tileId,
        outcome: battle.outcome!,
        rounds: battle.rounds.slice(),
        attackerStart: cloneGroups(battle.attackerStart),
        defenderStart: cloneGroups(battle.defenderStart),
        attackerFinal: cloneGroups(battle.attacker),
        defenderFinal: cloneGroups(battle.defender),
        startedAt,
        endedAt,
    };
}

function completeBattle(battle: BattleState, roster: UnitRoster): void {
    const attackerRemaining = roster.totalUnits(battle.attacker);
    const defenderRemaining = roster.totalUnits(battle.defender);
    const attackerStart = roster.totalUnits(battle.attackerStart);
    const defenderStart = roster.totalUnits(battle.defenderStart);
    battle.outcome = {
        winner:
            attackerRemaining > defenderRemaining
                ? 'attacker'
                : attackerRemaining < defenderRemaining
                  ? 'defender'
                  : 'draw',
        attackerRemaining,
        defenderRemaining,
        attackerLosses: Math.max(0, attackerStart - attackerRemaining),
        defenderLosses: Math.max(0, defenderStart - defenderRemaining),
    };
    battle.status = 'complete';
}

function applyCasualties(groups: UnitGroup[], kills: number): void {
    let remaining = kills;
    for (const group of groups) {
        if (remaining <= 0) break;
        const removed = Math.min(group.count, remaining);
        group.count -= removed;
        remaining -= removed;
    }
    for (let i = groups.length - 1; i >= 0; i -= 1) {
        if (groups[i].count <= 0) groups.splice(i, 1);
    }
}

function cloneGroups(groups: UnitGroup[]): UnitGroup[] {
    return groups.map((group) => ({ ...group }));
}

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function clamp01(value: number): number {
    return Math.max(0, Math.min(1, value));
}

function nextRand(battle: BattleState): number {
    let t = (battle.rngState + 0x6d2b79f5) | 0;
    battle.rngState = t;
    t = Math.imul(t ^ (t >>> 15), 1 | t);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}
